/**
 * Focus management for a dialog opened from any control (TMMRSL-0067 for a `RowActionsMenu` item,
 * generalised in TMMRSL-0068, DLG-004). The opener is recorded — a menu records its trigger in
 * `opener`, a plain button calls `remember(event.currentTarget)` — and when the dialog closes,
 * focus goes back to it. When the opener is no longer in the document (checked after the close
 * commits, so a handler that removes it and closes in one go is covered; `rowGone()` still works), focus
 * goes to `fallback` instead, the section's heading or group (`tabIndex={-1}`), so it is never lost to
 * the body.
 */
import { useCallback, useRef, type MutableRefObject, type RefObject } from 'react';

export interface MenuFocusReturn {
  /** Passed to each `RowActionsMenu`: the trigger of the menu that opened the dialog. */
  opener: MutableRefObject<HTMLElement | null>;
  /** A non-menu opener (a button) records itself here as it opens its dialog. */
  remember: (opener: HTMLElement | null) => void;
  /** Call when a dialog opened from a menu or a button closes. */
  dialogClosed: () => void;
  /** Call when the dialog's action removed the opener, so focus goes to the fallback instead. */
  rowGone: () => void;
}

export function useMenuFocusReturn(fallback: RefObject<HTMLElement | null>): MenuFocusReturn {
  const opener = useRef<HTMLElement | null>(null);
  const gone = useRef(false);
  const remember = useCallback((element: HTMLElement | null) => {
    opener.current = element;
  }, []);
  const dialogClosed = useCallback(() => {
    const trigger = opener.current;
    const removed = gone.current;
    gone.current = false;
    opener.current = null;
    // After MUI's own restore on the dialog's close, which targets a control that is unmounting.
    // Whether the opener is still in the document is read HERE, once React has committed the
    // removal a handler may have just caused (TMMRSL-0071): a caller that removes the opener and
    // closes in the same handler no longer has to call `rowGone()` first.
    window.setTimeout(() => {
      const target = !removed && trigger?.isConnected ? trigger : fallback.current;
      target?.focus();
    }, 0);
  }, [fallback]);
  const rowGone = useCallback(() => {
    gone.current = true;
  }, []);
  return { opener, remember, dialogClosed, rowGone };
}
